import React, { useState, useEffect } from 'react';
import { ChevronRight } from 'lucide-react';

/**
 * MinimalCollapse — luxe disclosure wrapper used only when the user is in
 * Minimal Mode (provider toggled the "Minimal" pill in the top toolbar).
 *
 * Behavior:
 *   • Normal mode (`data-minimalist="0"`):
 *       Renders children inline as if this wrapper didn't exist.
 *   • Minimal mode (`data-minimalist="1"`):
 *       Collapses the wrapped content under a sleek, well-labeled summary
 *       row. User taps the summary to expand. Hidden-but-available — the
 *       "minimalist room with drawers" pattern the provider asked for.
 *
 * Why a React component instead of pure CSS `<details>`:
 *   • Smooth open/close animation
 *   • Hooks into the global mood theme variables (`--card`, `--border`)
 *   • Survives the global `[data-minimalist]` mutation-observer pattern
 *     used elsewhere — no hot-reload weirdness from native disclosure elements
 *     pre-mounted in a closed state
 */
const MinimalCollapse = ({
  label,
  summary,            // optional secondary label shown to the right (e.g. "3 chips")
  defaultOpen = false,
  icon: Icon,         // optional lucide-react icon
  children,
  testid,
  // Force-show even in minimal (e.g. when the section is required)
  alwaysOpen = false,
}) => {
  const [minimal, setMinimal] = useState(
    typeof document !== 'undefined' &&
    document.documentElement.getAttribute('data-minimalist') === '1'
  );
  const [open, setOpen] = useState(defaultOpen);

  // Watch the <html data-minimalist> attribute so toggling Minimal in the
  // top toolbar instantly re-renders every wrapper across the page.
  useEffect(() => {
    const obs = new MutationObserver(() => {
      setMinimal(document.documentElement.getAttribute('data-minimalist') === '1');
    });
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['data-minimalist'] });
    return () => obs.disconnect();
  }, []);

  // Normal mode — pass through, zero overhead, no DOM wrapper change.
  if (!minimal || alwaysOpen) {
    return <>{children}</>;
  }

  // Minimal mode — render the luxe disclosure.
  return (
    <div
      className="border-2 border-amber-500/40 bg-slate-900/95 transition-all overflow-hidden"
      data-testid={testid || 'minimal-collapse'}
      data-open={open ? '1' : '0'}
      style={{
        // Subtle bevel even in minimal — gives the "drawer" feel without being noisy
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.08), 0 1px 0 rgba(0,0,0,0.4)',
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-3 py-2 hover:bg-slate-800/60 transition-colors group"
        data-testid={`${testid || 'minimal-collapse'}-toggle`}
        aria-expanded={open}
      >
        <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-amber-300 group-hover:text-amber-200">
          {Icon ? <Icon className="w-3.5 h-3.5" /> : null}
          {label}
          {summary ? (
            <span className="ml-1 px-1.5 py-0.5 text-[9px] font-bold bg-slate-700 text-amber-200 border border-amber-500/40">
              {summary}
            </span>
          ) : null}
        </span>
        <ChevronRight
          className={`w-4 h-4 text-amber-400 transition-transform duration-200 ${open ? 'rotate-90' : ''}`}
          aria-hidden="true"
        />
      </button>
      {open && (
        <div className="border-t-2 border-amber-500/30 p-2 animate-in slide-in-from-top-1 duration-200">
          {children}
        </div>
      )}
    </div>
  );
};

export default MinimalCollapse;
